/**
 * features/finance/LedgerReports.tsx
 * ------------------------------------------------------------------
 * The three statements the ledger exists to produce.
 *
 * One component, three views — trial balance, income & expenditure, balance
 * sheet — because they are the same figures cut three ways and a reader
 * moving between them should not find the totals disagreeing. Every number
 * on all three comes from one `buildTrialBalance` call.
 *
 * THE CAVEAT IS SHOWN BEFORE THE FIGURES, not after. A statement resting on
 * a ledger that does not balance is worse than no statement: it carries the
 * authority of a document without the arithmetic behind it. If the trial
 * balance is out, that is the first thing on the page and the export buttons
 * still work — refusing to export would only send somebody to a spreadsheet
 * where the warning does not follow them.
 *
 * THE UNCLOSED SURPLUS IS A NAMED LINE on the balance sheet, not a plug.
 * Before the year-end close, assets less liabilities exceeds the equity
 * accounts by exactly this year's trading, and the reader is better served
 * by seeing that figure and tying it to the income statement than by a sheet
 * that mysteriously balances.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Scale, TrendingUp, Landmark, AlertTriangle } from 'lucide-react';
import { useGlData } from '@/features/finance/useGlData';
import { buildTrialBalance, groupByType } from '@/lib/glReport';
import {
  buildIncomeStatement, buildBalanceSheet, statementCaveat,
  type StatementSection,
} from '@/lib/financialStatements';
import { downloadTablePdf, downloadCsv, type TableExport } from '@/lib/tableExport';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, ExportBar,
} from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';

export type LedgerView = 'trial' | 'incomeExp' | 'balanceSheet';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Blank rather than 0.00 — a column of zeros hides the figures that matter. */
const cell = (n: number) => (n === 0 ? '' : money(n));

const META: Record<LedgerView, { title: string; subtitle: string; icon: typeof Scale }> = {
  trial: {
    title: 'Trial Balance',
    subtitle: 'Every account with a balance, and proof the two sides agree',
    icon: Scale,
  },
  incomeExp: {
    title: 'Income & Expenditure',
    subtitle: 'What the school earned and spent over the year',
    icon: TrendingUp,
  },
  balanceSheet: {
    title: 'Balance Sheet',
    subtitle: 'What the school owns and owes today',
    icon: Landmark,
  },
};

export default function LedgerReports({ view }: { view: LedgerView }) {
  const [fy, setFy] = useState(`FY${new Date().getFullYear()}`);
  const gl = useGlData(fy);
  const meta = META[view];

  const tb = useMemo(
    () => buildTrialBalance(gl.accounts, gl.journals),
    [gl.accounts, gl.journals],
  );
  const income = useMemo(() => buildIncomeStatement(tb), [tb]);
  const sheet = useMemo(() => buildBalanceSheet(tb), [tb]);
  const groups = useMemo(() => groupByType(tb), [tb]);

  const caveat = statementCaveat(tb, view === 'balanceSheet' ? sheet : undefined);

  const years = Array.from({ length: 5 }, (_, i) => `FY${new Date().getFullYear() - i}`);

  function exportRows(): TableExport<string[]> {
    if (view === 'trial') {
      return {
        title: `Trial Balance — ${fy}`,
        columns: [
          { header: 'Code', text: (r: string[]) => r[0], width: 18 },
          { header: 'Account', text: (r: string[]) => r[1], width: 70 },
          { header: 'Debit', text: (r: string[]) => r[2], align: 'right', width: 26 },
          { header: 'Credit', text: (r: string[]) => r[3], align: 'right', width: 26 },
        ],
        rows: tb.rows.map((r) => [r.code, r.name, cell(r.dr), cell(r.cr)]),
        totals: ['', 'TOTAL', money(tb.totalDr), money(tb.totalCr)],
        note: caveat ?? undefined,
      };
    }
    const sections: StatementSection[] = view === 'incomeExp'
      ? [income.income, income.expenditure]
      : [sheet.assets, sheet.liabilities, sheet.equity];
    const rows: string[][] = [];
    for (const s of sections) {
      rows.push([s.label.toUpperCase(), '', '']);
      for (const l of s.lines) rows.push([l.code, l.name, money(l.amount)]);
      rows.push(['', `Total ${s.label.toLowerCase()}`, money(s.total)]);
    }
    if (view === 'incomeExp') {
      rows.push(['', income.isDeficit ? 'DEFICIT FOR THE YEAR' : 'SURPLUS FOR THE YEAR',
        money(Math.abs(income.surplus))]);
    } else {
      rows.push(['', 'Surplus for the year, not yet closed', money(sheet.unclosedSurplus)]);
      rows.push(['', 'NET ASSETS', money(sheet.netAssets)]);
      rows.push(['', 'TOTAL FUND', money(sheet.totalEquity)]);
    }
    return {
      title: `${meta.title} — ${fy}`,
      columns: [
        { header: '', text: (r: string[]) => r[0], width: 18 },
        { header: '', text: (r: string[]) => r[1], width: 80 },
        { header: 'Amount', text: (r: string[]) => r[2], align: 'right', width: 30 },
      ],
      rows,
      note: caveat ?? undefined,
    };
  }

  if (gl.loading) return <div className="p-8 flex justify-center"><Spinner /></div>;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={meta.icon}
        title={meta.title}
        subtitle={meta.subtitle}
        actions={(
          <div className="flex items-center gap-2">
            <select value={fy} onChange={(e) => setFy(e.target.value)} className={glassFieldClass('py-1.5')}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <ExportBar
              onPdf={() => downloadTablePdf(exportRows())}
              onCsv={() => downloadCsv(exportRows())}
              disabled={!tb.rows.length}
            />
          </div>
        )}
      />

      {/* SAID BEFORE THE FIGURES. A statement resting on a broken ledger
          carries the authority of a document without the arithmetic. */}
      {caveat && (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/40 px-4 py-3 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
          <p className="text-sm text-red-800 dark:text-red-200">{caveat}</p>
        </div>
      )}

      {view === 'incomeExp' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatChip icon={TrendingUp} label="Income" value={money(income.totalIncome)} />
            <StatChip icon={Scale} label="Expenditure" value={money(income.totalExpenditure)} />
            <StatChip
              icon={Landmark}
              label={income.isDeficit ? 'Deficit for the year' : 'Surplus for the year'}
              value={money(Math.abs(income.surplus))}
            />
          </div>
          <Section section={income.income} />
          <Section section={income.expenditure} />
          <SoftCard pad="p-5" hover={false}>
            <div className="flex items-baseline justify-between">
              <span className="font-semibold text-gray-800 dark:text-gray-100">
                {income.isDeficit ? 'Deficit for the year' : 'Surplus for the year'}
              </span>
              <span className={`text-xl font-semibold tabular-nums ${income.isDeficit
                ? 'text-red-600 dark:text-red-400'
                : 'text-emerald-600 dark:text-emerald-400'}`}
              >
                {money(Math.abs(income.surplus))}
              </span>
            </div>
          </SoftCard>
        </>
      )}

      {view === 'balanceSheet' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatChip icon={Landmark} label="Assets" value={money(sheet.totalAssets)} />
            <StatChip icon={Scale} label="Liabilities" value={money(sheet.totalLiabilities)} />
            <StatChip icon={TrendingUp} label="Net assets" value={money(sheet.netAssets)} />
          </div>
          <Section section={sheet.assets} />
          <Section section={sheet.liabilities} />
          <Section
            section={sheet.equity}
            /* NAMED, NOT PLUGGED. Before the year-end close this is where
               the year's trading sits, and a reader should be able to tie
               it straight to the income statement. */
            extra={sheet.unclosedSurplus !== 0
              ? { label: 'Surplus for the year, not yet closed', amount: sheet.unclosedSurplus }
              : undefined}
            grandTotal={{ label: 'Total fund', amount: sheet.totalEquity }}
          />
          <SoftCard pad="p-5" hover={false}>
            <div className="flex items-baseline justify-between">
              <span className="font-semibold text-gray-800 dark:text-gray-100">
                Net assets less the fund
              </span>
              <span className={`text-lg font-semibold tabular-nums ${sheet.balanced
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-red-600 dark:text-red-400'}`}
              >
                {sheet.balanced ? 'Balances' : money(sheet.difference)}
              </span>
            </div>
          </SoftCard>
        </>
      )}

      {view === 'trial' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatChip icon={Scale} label="Total debits" value={money(tb.totalDr)} />
            <StatChip icon={Scale} label="Total credits" value={money(tb.totalCr)} />
            <StatChip
              icon={tb.balanced ? Landmark : AlertTriangle}
              label={tb.balanced ? 'In balance' : 'OUT OF BALANCE'}
              value={tb.balanced ? '—' : money(Math.abs(tb.difference))}
            />
          </div>
          {groups.map((g) => (
            <SoftCard key={g.type} pad="p-0" hover={false}>
              <PanelHeader icon={Scale} title={g.label} />
              <div className="p-5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      <th className="py-2 pr-4">Code</th>
                      <th className="py-2 pr-4">Account</th>
                      <th className="py-2 pr-4 text-right">Debit</th>
                      <th className="py-2 text-right">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={r.code} className="border-b border-gray-50 dark:border-gray-800">
                        <td className="py-2 pr-4 font-mono text-xs">{r.code}</td>
                        <td className="py-2 pr-4">{r.name}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{cell(r.dr)}</td>
                        <td className="py-2 text-right tabular-nums">{cell(r.cr)}</td>
                      </tr>
                    ))}
                    <tr className="font-semibold">
                      <td className="py-2 pr-4" colSpan={2}>Total {g.label.toLowerCase()}</td>
                      <td className="py-2 pr-4 text-right tabular-nums" colSpan={2}>{money(g.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </SoftCard>
          ))}
        </>
      )}
    </div>
  );
}

function Section({
  section, extra, grandTotal,
}: {
  section: StatementSection;
  extra?: { label: string; amount: number };
  grandTotal?: { label: string; amount: number };
}) {
  return (
    <SoftCard pad="p-0" hover={false}>
      <PanelHeader icon={Scale} title={section.label} />
      <div className="p-5 overflow-x-auto">
        <table className="w-full text-sm">
          <tbody>
            {section.lines.map((l) => (
              <tr key={l.code} className="border-b border-gray-50 dark:border-gray-800">
                <td className="py-2 pr-4 font-mono text-xs w-16">{l.code}</td>
                <td className="py-2 pr-4">{l.name}</td>
                <td className={`py-2 text-right tabular-nums ${l.amount < 0 ? 'text-gray-500' : ''}`}>
                  {money(l.amount)}
                </td>
              </tr>
            ))}
            {section.lines.length === 0 && (
              <tr><td className="py-2 text-gray-400" colSpan={3}>Nothing posted.</td></tr>
            )}
            <tr className="font-semibold">
              <td className="py-2 pr-4" colSpan={2}>Total {section.label.toLowerCase()}</td>
              <td className="py-2 text-right tabular-nums">{money(section.total)}</td>
            </tr>
            {extra && (
              <tr>
                <td className="py-2 pr-4" colSpan={2}>{extra.label}</td>
                <td className="py-2 text-right tabular-nums">{money(extra.amount)}</td>
              </tr>
            )}
            {grandTotal && (
              <tr className="font-semibold border-t-2 border-gray-200 dark:border-gray-700">
                <td className="py-2 pr-4" colSpan={2}>{grandTotal.label}</td>
                <td className="py-2 text-right tabular-nums">{money(grandTotal.amount)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </SoftCard>
  );
}
